import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useMaintenance } from '../context/MaintenanceContext';
import { PageTransition, FadeIn } from '../components/layout';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ChevronRight, ShoppingBag, Wrench, Users, Star, Palette, FolderTree, Gift, FileText, MessageSquareQuote, ShieldAlert, Loader2 } from 'lucide-react';

export default function AdminPage() {
  const { user, token } = useAuth();
  const {
    maintenanceEnabled,
    maintenanceUpdatedAt,
    isLoadingMaintenance,
    isUpdatingMaintenance,
    updateMaintenanceStatus
  } = useMaintenance();

  const formattedUpdatedAt = maintenanceUpdatedAt
    ? new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(maintenanceUpdatedAt))
    : null;

  const adminLinks = [
    {
      title: 'Commandes Press On Nails',
      description: 'Gérer les commandes, suivre les statuts, voir les détails',
      icon: ShoppingBag,
      href: '/admin/commandes',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: 'Prestations & Tarifs',
      description: 'Créer, modifier et organiser vos prestations par catégorie',
      icon: Wrench,
      href: '/admin/prestations',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: 'Sous-catégories',
      description: 'Organiser les prestations en sous-catégories (Gainage, Gel X...)',
      icon: FolderTree,
      href: '/admin/subcategories',
      color: 'bg-teal-50 text-teal-600'
    },
    {
      title: 'Cartes Cadeaux',
      description: 'Gérer les montants prédéfinis et les prestations éligibles',
      icon: Gift,
      href: '/admin/gift-cards',
      color: 'bg-rose-50 text-rose-600'
    },
    {
      title: 'Press On Nails - Config',
      description: 'Gérer les formes, couleurs, longueurs et options du formulaire',
      icon: Palette,
      href: '/admin/press-on-nails',
      color: 'bg-pink-50 text-pink-600'
    },
    {
      title: 'Prestations Signature',
      description: 'Sélectionner les prestations mises en avant sur le site',
      icon: Star,
      href: '/admin/featured',
      color: 'bg-amber-50 text-amber-600'
    },
    {
      title: 'Documents B2B',
      description: 'Gérer les documents téléchargeables pour l\'Atelier Événementiel',
      icon: FileText,
      href: '/admin/b2b-documents',
      color: 'bg-cyan-50 text-cyan-600'
    },
    {
      title: 'Inscriptions Formation',
      description: 'Consulter les contacts, messages et formations souhaitées',
      icon: Users,
      href: '/admin/formations',
      color: 'bg-indigo-50 text-indigo-600'
    },
    {
      title: 'Avis Clients',
      description: 'Gérer les témoignages affichés sur la page d\'accueil',
      icon: MessageSquareQuote,
      href: '/admin/reviews',
      color: 'bg-emerald-50 text-emerald-600'
    }
  ];

  const handleMaintenanceToggle = async (checked) => {
    try {
      await updateMaintenanceStatus(checked, token);
      toast.success(
        checked
          ? 'Mode maintenance activé sur le site public'
          : 'Mode maintenance désactivé'
      );
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      toast.error(error.response?.data?.detail || 'Impossible de mettre à jour le mode maintenance');
    }
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-[var(--charcoal)]" data-testid="admin-hero">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Administration
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white/60"
          >
            Bienvenue, {user?.first_name} !
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Administration</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-16 px-6" data-testid="admin-content">
        <div className="max-w-[800px] mx-auto">
          <FadeIn>
            <h2 className="font-['Cormorant_Garamond'] text-2xl mb-8 text-[var(--charcoal)]">
              Tableau de bord
            </h2>
          </FadeIn>

          <FadeIn delay={0.05}>
            <div className="mb-10 border border-[var(--champagne)] bg-white shadow-sm">
              <div className="flex flex-col gap-6 border-b border-[var(--champagne)]/80 px-8 py-8 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-4">
                  <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${maintenanceEnabled ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    <ShieldAlert size={24} />
                  </div>
                  <div>
                    <h3 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
                      Mode maintenance global
                    </h3>
                    <p className="mt-2 max-w-2xl text-sm leading-7 text-[var(--charcoal)]/65">
                      Coupe l’accès au site public avec redirection vers une page de maintenance, tout en laissant
                      l’administration et la page de login accessibles.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 self-start md:self-center">
                  <div className="text-right">
                    <p className={`text-sm font-medium ${maintenanceEnabled ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {maintenanceEnabled ? 'Activé' : 'Désactivé'}
                    </p>
                    <p className="text-xs uppercase tracking-[0.2em] text-[var(--charcoal)]/45">
                      Global et persistant
                    </p>
                  </div>

                  {isUpdatingMaintenance ? (
                    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--champagne)] bg-[var(--ivory)] text-[var(--charcoal)]">
                      <Loader2 size={18} className="animate-spin" />
                    </div>
                  ) : (
                    <Switch
                      checked={maintenanceEnabled}
                      disabled={isLoadingMaintenance}
                      onCheckedChange={handleMaintenanceToggle}
                      data-testid="maintenance-mode-switch"
                    />
                  )}
                </div>
              </div>

              <div className="grid gap-4 bg-[var(--ivory)]/70 px-8 py-6 text-sm text-[var(--charcoal)]/70 md:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[var(--charcoal)]/45">Impact</p>
                  <p className="mt-2">Toutes les routes publiques basculent vers la page de maintenance.</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[var(--charcoal)]/45">Exceptions</p>
                  <p className="mt-2">`/admin` et `/login` restent opérationnels pendant toute l’intervention.</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[var(--charcoal)]/45">Dernière mise à jour</p>
                  <p className="mt-2">{formattedUpdatedAt || 'Aucune modification enregistrée pour le moment.'}</p>
                </div>
              </div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {adminLinks.map((link, index) => (
              <FadeIn key={link.href} delay={index * 0.1}>
                <Link
                  to={link.href}
                  className="block bg-white p-8 border border-[var(--champagne)] hover:border-[var(--gold)]/30 hover:shadow-lg transition-all group"
                  data-testid={`admin-link-${link.href.split('/').pop()}`}
                >
                  <div className={`w-14 h-14 rounded-full ${link.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <link.icon size={24} />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-2 text-[var(--charcoal)] group-hover:text-[var(--gold)] transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-sm text-[var(--charcoal)]/60">
                    {link.description}
                  </p>
                  <div className="mt-4 text-[var(--gold)] text-sm font-medium flex items-center gap-1">
                    Accéder
                    <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>

          {/* Quick Stats Placeholder */}
          <FadeIn delay={0.3}>
            <div className="mt-12 p-8 bg-[var(--champagne)]/20 border border-[var(--champagne)]">
              <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                Statistiques rapides
              </h3>
              <p className="text-sm text-[var(--charcoal)]/60">
                Les statistiques détaillées seront disponibles prochainement.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageTransition>
  );
}
